import React, { useState, useEffect } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Container, Typography, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Stack, IconButton, Tooltip, TablePagination,
  Grid, Tabs, Tab, Alert, CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  collection, onSnapshot, query, orderBy, doc, updateDoc,
  addDoc, serverTimestamp, where,
} from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function PayoutTracker() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0=pending, 1=processing, 2=completed, 3=failed
  const [declineDialog, setDeclineDialog] = useState({ open: false, payout: null, reason: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const STATUSES = ["pending", "processing", "completed", "failed"];

  useEffect(() => {
    const q = query(collection(db, "PayoutRequests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setPayouts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const filtered = payouts.filter((p) => (p.status || "pending") === STATUSES[tab]);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totals = {
    pending: payouts.filter((p) => p.status === "pending").reduce((s, p) => s + (p.amount || 0), 0),
    completed: payouts.filter((p) => p.status === "completed").reduce((s, p) => s + (p.amount || 0), 0),
  };

  const counts = STATUSES.map((s) => payouts.filter((p) => (p.status || "pending") === s).length);

  const handleApprove = async (payout) => {
    try {
      await updateDoc(doc(db, "PayoutRequests", payout.id), {
        status: "processing",
        approvedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "payout-approve",
        targetName: payout.creatorEmail || payout.creatorId,
        details: { amount: payout.amount },
        timestamp: serverTimestamp(),
      });
      toast.success(`Payout of $${payout.amount} approved — Cloud Function will process via PayPal`);
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleDecline = async () => {
    try {
      await updateDoc(doc(db, "PayoutRequests", declineDialog.payout.id), {
        status: "failed",
        declinedAt: serverTimestamp(),
        declineReason: declineDialog.reason,
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "payout-decline",
        targetName: declineDialog.payout.creatorEmail,
        details: { amount: declineDialog.payout.amount, reason: declineDialog.reason },
        timestamp: serverTimestamp(),
      });
      toast.success("Payout declined");
      setDeclineDialog({ open: false, payout: null, reason: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    return (ts.toDate ? ts.toDate() : new Date(ts)).toLocaleDateString();
  };

  const STATUS_COLORS = { pending: "warning", processing: "info", completed: "success", failed: "error" };

  return (
    <Page title="Payout Tracker | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Creator Payout Tracker</Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve creator/guru payout requests — processed via PayPal Cloud Function
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>How it works:</strong> Approving a request sets status to "processing", which triggers the PayPal Cloud Function in <code>functions/index.js</code> to automatically send the payout.
        </Alert>

        {/* Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#FFC107" }}>${totals.pending.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#3dc10e" }}>${totals.completed.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">Paid Out (All Time)</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{counts[0]}</Typography>
              <Typography variant="caption" color="text.secondary">Awaiting Approval</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{counts[2]}</Typography>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
            </Card>
          </Grid>
        </Grid>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} sx={{ mb: 3 }}>
          {STATUSES.map((s, i) => (
            <Tab key={s} label={`${s.charAt(0).toUpperCase() + s.slice(1)} (${counts[i]})`} />
          ))}
        </Tabs>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Creator</TableCell>
                    <TableCell>PayPal Email</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Note</TableCell>
                    {tab === 0 && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((payout) => (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar src={payout.creatorPhoto} sx={{ width: 28, height: 28, fontSize: 12 }}>
                            {(payout.creatorName || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{payout.creatorName || "—"}</Typography>
                            <Typography variant="caption" color="text.secondary">{payout.creatorEmail || "—"}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{payout.paypalEmail || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#3dc10e" }}>
                          ${(payout.amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={payout.status || "pending"} size="small"
                          color={STATUS_COLORS[payout.status] || "default"} sx={{ textTransform: "capitalize" }} />
                      </TableCell>
                      <TableCell>{formatTime(payout.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {payout.declineReason || payout.note || "—"}
                        </Typography>
                      </TableCell>
                      {tab === 0 && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" variant="contained"
                              sx={{ bgcolor: "#3dc10e", "&:hover": { bgcolor: "#097936" } }}
                              onClick={() => handleApprove(payout)}>
                              Approve
                            </Button>
                            <Button size="small" variant="outlined" color="error"
                              onClick={() => setDeclineDialog({ open: true, payout, reason: "" })}>
                              Decline
                            </Button>
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={tab === 0 ? 7 : 6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading..." : "No payouts in this status"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination rowsPerPageOptions={[15, 25, 50]} component="div"
            count={filtered.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </Card>
      </Container>

      <Dialog open={declineDialog.open} onClose={() => setDeclineDialog({ open: false, payout: null, reason: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Decline Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Decline <strong>{declineDialog.payout?.creatorName}</strong>'s payout of <strong>${declineDialog.payout?.amount}</strong>?
          </Typography>
          <TextField fullWidth label="Reason" value={declineDialog.reason}
            onChange={(e) => setDeclineDialog((p) => ({ ...p, reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialog({ open: false, payout: null, reason: "" })}>Back</Button>
          <Button variant="contained" color="error" onClick={handleDecline}>Decline</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
